// Maya vendedora: bandera, textos en seis idiomas y embudo de ventas.
//
// Todo el flujo de venta dentro del chat vive detrás de una bandera:
//   - VITE_MAYA_SALES="true" (entorno), o
//   - ?mayasales=1 en la URL (se recuerda en la pestaña; ?mayasales=0 lo apaga).
// Con la bandera apagada Maya se comporta exactamente como antes.
import { logMayaEvent } from "@/lib/maya-tracking.functions";
import { getMayaAnonSessionId } from "@/lib/maya-session";

const FLAG_KEY = "mm:maya-sales";
const PENDING_KEY = "mm:maya-sale-pending:v1";

export function isMayaSalesEnabled(): boolean {
  if (import.meta.env["VITE_MAYA_SALES"] === "true") return true;
  if (typeof window === "undefined") return false;
  // En el sitio publicado el parámetro de prueba no enciende Maya Sales.
  if (import.meta.env.PROD) return false;
  try {
    const q = new URLSearchParams(window.location.search).get("mayasales");
    if (q === "1") window.sessionStorage.setItem(FLAG_KEY, "1");
    if (q === "0") window.sessionStorage.removeItem(FLAG_KEY);
    return window.sessionStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

export type MayaSaleEvent =
  | "maya_sale_search"
  | "maya_sale_tour_recommended"
  | "maya_sale_tour_viewed"
  | "maya_sale_date_selected"
  | "maya_sale_checkout_started"
  | "maya_sale_payment_approved"
  | "maya_sale_payment_declined"
  | "maya_sale_booking_confirmed"
  | "maya_sale_booking_abandoned"
  | "maya_sale_recovered";

/** Sin datos personales: solo producto, importes y conteos. */
export function trackMayaSale(
  event: MayaSaleEvent,
  meta: {
    tourSlug?: string;
    destination?: string;
    amount?: number;
    currency?: string;
    pax?: number;
    lang?: string;
    code?: string;
  } = {},
): void {
  try {
    void logMayaEvent({
      data: {
        event_type: event,
        anon_session_id: getMayaAnonSessionId(),
        destination: meta.destination?.slice(0, 120),
        lang: meta.lang,
        amount_cents: meta.amount != null ? Math.round(meta.amount * 100) : undefined,
        currency: meta.currency,
        metadata: {
          tour_slug: meta.tourSlug,
          pax: meta.pax,
          code: meta.code,
          source: "maya_sales",
        },
      },
    }).catch(() => {});
  } catch {
    /* la analítica nunca bloquea la venta */
  }
}

/** Marca local de que el pago salió de Maya (para registrar el resultado). */
export interface PendingMayaSale {
  tourSlug: string;
  tourTitle: string;
  amount: number | null;
  currency: string;
  pax: number;
  date?: string | null;
  startTime?: string | null;
  startedAt: string;
}

export function setPendingMayaSale(p: PendingMayaSale): void {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    /* noop */
  }
}

export function readPendingMayaSale(): PendingMayaSale | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingMayaSale) : null;
  } catch {
    return null;
  }
}

export function takePendingMayaSale(): PendingMayaSale | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw) as PendingMayaSale;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Textos (seis idiomas). Se toman del idioma global del sitio.
// ---------------------------------------------------------------------------
type Lang = "es" | "en" | "fr" | "de" | "it" | "pt";

const T = {
  bookNow: {
    es: "Reservar ahora",
    en: "Book now",
    fr: "Réserver",
    de: "Jetzt buchen",
    it: "Prenota ora",
    pt: "Reservar agora",
  },
  details: {
    es: "Ver detalles",
    en: "View details",
    fr: "Voir les détails",
    de: "Details ansehen",
    it: "Vedi dettagli",
    pt: "Ver detalhes",
  },
  perPerson: {
    es: "por persona",
    en: "per person",
    fr: "par personne",
    de: "pro Person",
    it: "a persona",
    pt: "por pessoa",
  },
  from: { es: "Precio", en: "Price", fr: "Prix", de: "Preis", it: "Prezzo", pt: "Preço" },
  estTotal: {
    es: "Total estimado",
    en: "Estimated total",
    fr: "Total estimé",
    de: "Geschätzter Gesamtpreis",
    it: "Totale stimato",
    pt: "Total estimado",
  },
  totalOnDate: {
    es: "Total exacto al elegir fecha",
    en: "Exact total once you pick a date",
    fr: "Total exact après choix de la date",
    de: "Genauer Preis nach Datumswahl",
    it: "Totale esatto scelta la data",
    pt: "Total exato ao escolher a data",
  },
  schedule: { es: "Horario", en: "Schedule", fr: "Horaire", de: "Uhrzeit", it: "Orario", pt: "Horário" },
  pickup: { es: "Recogida", en: "Pickup", fr: "Prise en charge", de: "Abholung", it: "Ritiro", pt: "Busca" },
  meeting: {
    es: "Punto de encuentro",
    en: "Meeting point",
    fr: "Point de rendez-vous",
    de: "Treffpunkt",
    it: "Punto d'incontro",
    pt: "Ponto de encontro",
  },
  included: { es: "Incluye", en: "Includes", fr: "Inclus", de: "Inklusive", it: "Include", pt: "Inclui" },
  payApart: {
    es: "Se paga aparte",
    en: "Paid separately",
    fr: "À payer sur place",
    de: "Separat zu zahlen",
    it: "Da pagare a parte",
    pt: "Pago à parte",
  },
  cancellation: {
    es: "Cancelación",
    en: "Cancellation",
    fr: "Annulation",
    de: "Stornierung",
    it: "Cancellazione",
    pt: "Cancelamento",
  },
  why: { es: "Por qué te la recomiendo", en: "Why I recommend it", fr: "Pourquoi je la recommande", de: "Warum ich es empfehle", it: "Perché te lo consiglio", pt: "Por que recomendo" },
  continueBooking: {
    es: "Continuar con la reserva",
    en: "Continue booking",
    fr: "Continuer la réservation",
    de: "Buchung fortsetzen",
    it: "Continua la prenotazione",
    pt: "Continuar a reserva",
  },
  pickDate: {
    es: "Elige fecha, horario y personas",
    en: "Choose date, time and travelers",
    fr: "Choisis date, horaire et voyageurs",
    de: "Datum, Uhrzeit und Personen wählen",
    it: "Scegli data, orario e persone",
    pt: "Escolha data, horário e pessoas",
  },
  summary: { es: "Resumen", en: "Summary", fr: "Récapitulatif", de: "Zusammenfassung", it: "Riepilogo", pt: "Resumo" },
  payOnline: {
    es: "Se paga en línea",
    en: "Paid online",
    fr: "Payé en ligne",
    de: "Online bezahlt",
    it: "Pagato online",
    pt: "Pago online",
  },
  notReady: {
    es: "¿Aún no estás listo?",
    en: "Not ready yet?",
    fr: "Pas encore prêt ?",
    de: "Noch nicht bereit?",
    it: "Non sei ancora pronto?",
    pt: "Ainda não está pronto?",
  },
  save: { es: "Guardar selección", en: "Save selection", fr: "Enregistrer", de: "Auswahl speichern", it: "Salva selezione", pt: "Salvar seleção" },
  saved: { es: "Guardado ✓", en: "Saved ✓", fr: "Enregistré ✓", de: "Gespeichert ✓", it: "Salvato ✓", pt: "Salvo ✓" },
  sendWa: {
    es: "Enviarme por WhatsApp",
    en: "Send to my WhatsApp",
    fr: "M'envoyer sur WhatsApp",
    de: "Per WhatsApp senden",
    it: "Invia su WhatsApp",
    pt: "Enviar por WhatsApp",
  },
  compare: {
    es: "Comparar con otra",
    en: "Compare with another",
    fr: "Comparer",
    de: "Vergleichen",
    it: "Confronta",
    pt: "Comparar",
  },
  advisor: {
    es: "Hablar con un asesor",
    en: "Talk to an advisor",
    fr: "Parler à un conseiller",
    de: "Mit Berater sprechen",
    it: "Parla con un consulente",
    pt: "Falar com um consultor",
  },
  back: { es: "Volver al chat", en: "Back to chat", fr: "Retour au chat", de: "Zurück zum Chat", it: "Torna alla chat", pt: "Voltar ao chat" },
  error: {
    es: "No pude confirmar la disponibilidad en este momento. Tus datos siguen guardados. Puedes intentarlo nuevamente o solicitar ayuda por WhatsApp.",
    en: "I couldn't confirm availability right now. Your details are still saved. You can try again or ask for help on WhatsApp.",
    fr: "Je n'ai pas pu confirmer la disponibilité pour le moment. Tes données restent enregistrées. Tu peux réessayer ou demander de l'aide sur WhatsApp.",
    de: "Ich konnte die Verfügbarkeit gerade nicht bestätigen. Deine Angaben bleiben gespeichert. Versuche es erneut oder bitte per WhatsApp um Hilfe.",
    it: "Non sono riuscita a confermare la disponibilità in questo momento. I tuoi dati restano salvati. Puoi riprovare o chiedere aiuto su WhatsApp.",
    pt: "Não consegui confirmar a disponibilidade agora. Seus dados continuam salvos. Você pode tentar novamente ou pedir ajuda pelo WhatsApp.",
  },
  retry: { es: "Intentar de nuevo", en: "Try again", fr: "Réessayer", de: "Erneut versuchen", it: "Riprova", pt: "Tentar novamente" },
  noHold: {
    es: "El lugar se aparta solo al pagar; la disponibilidad se vuelve a comprobar en ese momento.",
    en: "Your spot is only held once you pay; availability is re-checked at that moment.",
    fr: "La place n'est réservée qu'au paiement ; la disponibilité est revérifiée à ce moment-là.",
    de: "Der Platz wird erst bei Zahlung reserviert; die Verfügbarkeit wird dann erneut geprüft.",
    it: "Il posto viene bloccato solo al pagamento; la disponibilità viene ricontrollata in quel momento.",
    pt: "A vaga só é reservada no pagamento; a disponibilidade é verificada novamente nesse momento.",
  },
  addonTitle: {
    es: "También podría servirte",
    en: "You might also like",
    fr: "Cela pourrait aussi te plaire",
    de: "Das könnte dir auch gefallen",
    it: "Potrebbe interessarti anche",
    pt: "Também pode te interessar",
  },
  recover: {
    es: "¿Quieres continuar con tu reserva de {tour}{date}? Todavía puedo ayudarte a comprobar la disponibilidad.",
    en: "Would you like to continue your booking for {tour}{date}? I can still help you check availability.",
    fr: "Veux-tu continuer ta réservation pour {tour}{date} ? Je peux encore t'aider à vérifier la disponibilité.",
    de: "Möchtest du deine Buchung für {tour}{date} fortsetzen? Ich kann dir weiterhin helfen, die Verfügbarkeit zu prüfen.",
    it: "Vuoi continuare la prenotazione di {tour}{date}? Posso ancora aiutarti a verificare la disponibilità.",
    pt: "Quer continuar sua reserva de {tour}{date}? Ainda posso ajudar você a verificar a disponibilidade.",
  },
  forDate: { es: " para el ", en: " for ", fr: " pour le ", de: " am ", it: " per il ", pt: " para " },
  continue: { es: "Continuar", en: "Continue", fr: "Continuer", de: "Fortsetzen", it: "Continua", pt: "Continuar" },
  greet: {
    es: "Hola, soy Maya, tu guía digital de Mostrando México. ¿Qué te gustaría vivir en México?",
    en: "Hi, I'm Maya, your digital guide at Mostrando México. What would you like to experience in Mexico?",
    fr: "Bonjour, je suis Maya, ton guide numérique de Mostrando México. Qu'aimerais-tu vivre au Mexique ?",
    de: "Hallo, ich bin Maya, deine digitale Reiseführerin von Mostrando México. Was möchtest du in Mexiko erleben?",
    it: "Ciao, sono Maya, la tua guida digitale di Mostrando México. Cosa ti piacerebbe vivere in Messico?",
    pt: "Olá, sou a Maya, sua guia digital da Mostrando México. O que você gostaria de viver no México?",
  },
  haveBooking: { es: "Ya tengo una reserva", en: "I already have a booking", fr: "J'ai déjà une réservation", de: "Ich habe schon eine Buchung", it: "Ho già una prenotazione", pt: "Já tenho uma reserva" },
  haveBookingMsg: {
    es: "Ya tengo una reserva y quiero consultarla.",
    en: "I already have a booking and want to check it.",
    fr: "J'ai déjà une réservation et je veux la consulter.",
    de: "Ich habe schon eine Buchung und möchte sie ansehen.",
    it: "Ho già una prenotazione e voglio consultarla.",
    pt: "Já tenho uma reserva e quero consultá-la.",
  },
  human: { es: "Hablar con una persona", en: "Talk to a person", fr: "Parler à une personne", de: "Mit einer Person sprechen", it: "Parla con una persona", pt: "Falar com uma pessoa" },
  typing: { es: "Maya está escribiendo…", en: "Maya is typing…", fr: "Maya écrit…", de: "Maya schreibt…", it: "Maya sta scrivendo…", pt: "Maya está digitando…" },
  free: { es: "Gratis", en: "Free", fr: "Gratuit", de: "Kostenlos", it: "Gratis", pt: "Grátis" },
  placeholder: { es: "Escribe aquí…", en: "Type here…", fr: "Écris ici…", de: "Hier schreiben…", it: "Scrivi qui…", pt: "Escreva aqui…" },
  sandbox: {
    es: "Modo de pruebas: no se realizan cobros reales.",
    en: "Test mode: no real charges are made.",
    fr: "Mode test : aucun paiement réel.",
    de: "Testmodus: keine echten Zahlungen.",
    it: "Modalità test: nessun addebito reale.",
    pt: "Modo de teste: nenhuma cobrança real.",
  },
} as const;

export type MayaSalesKey = keyof typeof T;

export function ms(lang: string, key: MayaSalesKey, vars: Record<string, string> = {}): string {
  const l = (["es", "en", "fr", "de", "it", "pt"].includes(lang) ? lang : "es") as Lang;
  let s: string = T[key][l];
  for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, v);
  return s;
}
